import { computed, inject } from '@angular/core';
import { PunchOutIntegration } from '@idealsupply/ngclient-webservice-shopping-cart';
import { signalStore, withComputed, withMethods, withProps } from '@ngrx/signals';
import { CartComponentStore, CartsStore } from 'cart-data';
import { APP_CONFIG } from 'config-data';
import { GUEST_CUSTOMER } from 'customers-data';
import { Hash } from 'fast-sha256';
import { ItemId } from 'products-data';
import { withSelectableEntityStore } from 'state-data';
import { SHOPPING_CART_API_PATH } from '../constants';
import { PunchOutIntegrationStore } from './punchout-integrations.store';

interface IPopup {
  poi: PunchOutIntegration;
  popup: Window;
}

/**
 * Event dispatched on the opener window by `refresh-cart.html`, which the
 * shopping-cart webservice redirects a vendor punchout popup to once the
 * vendor's cart has been merged into the IdealLINK cart. `detail` is the
 * numeric id of the cart that was updated.
 */
const CART_REFRESH_EVENT = 'ideal-cart-refresh';

export const PunchOutIntegrationComponentStore = signalStore(
  withProps(() => {
    return {
      _openPopups: {} as Record<number, IPopup>,
    };
  }),
  withSelectableEntityStore('PunchOutIntegrations', {
    selectEntityById: (id: string) => inject(PunchOutIntegrationStore).get(id),
    protected: true,
  }),
  withComputed((store) => {
    const _record = computed(() => store._selectedPunchOutIntegrations());
    const loading = computed(() => _record()?.loading ?? false);
    const error = computed(() => _record()?.error ?? null);
    const success = computed(() => !error() && !!_record()?.entity);
    const integrations = computed(() => _record()?.entity?.integrations ?? []);
    return {
      loading,
      error,
      success,
      integrations,
    };
  }),
  withMethods((store) => {
    const guestCustomerNumber = inject(GUEST_CUSTOMER);
    const cartStore = inject(CartComponentStore);
    const cartsStore = inject(CartsStore);
    const appConfig = inject(APP_CONFIG);
    const apiRoot = appConfig.auth?.apiRoot ?? 'https://api.idealsupply.com';
    const baseUrl = `${apiRoot}${SHOPPING_CART_API_PATH}`;

    const selectPunchOutIntegrationByCustomerNumber = (select?: string) => {
      store._setPunchOutIntegrationsId(select ?? guestCustomerNumber);
    };

    const isPunchOutIntegrationOpen = (integrationId: number) => {
      return !!store._openPopups[integrationId];
    };

    const getOpenPopupIntegration = (integrationId: number) => {
      return store._openPopups[integrationId];
    };

    const handleSchneiderEZMessageReceived = (
      data: {
        designation: string;
        catalogNo: string;
        gtin: string;
        quantity: number;
      }[],
    ) => {
      const cartItems = cartStore.cartItems();
      data.forEach((item) => {
        const id = new ItemId('3SQ', item.catalogNo);
        const existing = cartItems.find((i) => id.equals(i.id));
        const currentCount = existing?.quantity ?? 0;
        const total = currentCount + item.quantity;
        cartStore.updateItemQuantity(id, total);
      });
    };

    const handlePunchoutMessageReceived = (popup: Window, event: MessageEvent) => {
      if (event.isTrusted && event.origin === 'https://ezlist.schneider-electric.com') {
        switch (event.data.fn) {
          case 'cnsAddToProject':
            if (event.data.IsValid) {
              handleSchneiderEZMessageReceived(JSON.parse(event.data.data));
            }
            popup.close();
            break;
        }
      }
    };

    const _openPopup = (poi: PunchOutIntegration): Window | null => {
      const integrationId = poi.id!;
      const cart = cartStore.cart();
      const selectedCartId = cartStore.cartId();
      if (cart && selectedCartId) {
        const hashParts = [cart.id, cart.type || '', cart.customerNumber || ''];
        const hash = new Hash();
        hash.update(new TextEncoder().encode(hashParts.join('|')));
        const h = btoa(String.fromCharCode.apply(null, hash.digest() as any));
        const url =
          poi.url?.startsWith('https://') || poi.url?.startsWith('http://') || poi.url?.startsWith('//') || poi.url?.startsWith('./')
            ? poi.url
            : `${baseUrl}${poi.url}/${cart.id}?hash=${h}`;

        const popupId = `ideal_poi_popup_${integrationId}`;
        const features = `popup=true,width=${poi.width || 1050},height=${poi.height || 800}`;
        const popup = window.open(url, popupId, features);
        if (popup) {
          const eventListener = (event: MessageEvent) => {
            handlePunchoutMessageReceived(popup, event);
          };
          const refreshListener = (event: Event) => {
            const refreshedCartId = (event as CustomEvent<number>).detail;
            if (refreshedCartId !== cart.id) {
              console.warn(`Punchout refreshed cart ${refreshedCartId} but the popup was opened for cart ${cart.id}`);
            }
            cartsStore.reloadCart(selectedCartId);
          };
          const interval = setInterval(() => {
            if (popup.closed) {
              clearInterval(interval);
              window.removeEventListener(CART_REFRESH_EVENT, refreshListener);
              delete store._openPopups[integrationId];
            }
          }, 500);
          popup.addEventListener('message', eventListener);
          window.addEventListener(CART_REFRESH_EVENT, refreshListener);
        }
        return popup;
      }
      return null;
    };

    const openPunchOutIntegration = (integrationId: number) => {
      if (!isPunchOutIntegrationOpen(integrationId)) {
        const poi = store.integrations().find((i) => i.id === integrationId);
        if (poi) {
          const popup = _openPopup(poi);
          if (popup) {
            store._openPopups[integrationId] = {
              poi,
              popup,
            };
          }
        }
      }
      const opened = getOpenPopupIntegration(integrationId);
      if (opened) {
        opened.popup.focus();
      }
    };

    return {
      selectPunchOutIntegrationByCustomerNumber,
      isPunchOutIntegrationOpen,
      openPunchOutIntegration,
      getOpenPopupIntegration,
    };
  }),
);
